"use client";
import { EditResourceRequest, IResource } from "@gettactic/api";
import { LoadingSpinner } from "@gettactic/components/src/loadings/LoadingSpinner/LoadingSpinner";
import { logError } from "@gettactic/helpers";
import { hasEnabledDeleteResource } from "@gettactic/helpers/src/featureFlags";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import { useAreas, useAuthenticated } from "@gettactic/hooks";
import { Trash2 } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { DeleteResourceModal } from "@/features/reserve/components/DeleteResourceModal";
import {
  EditResourceForm,
  getResourceType
} from "@/features/reserve/components/ResourceSettingsPanel/ResourceSettingsPanel";
import { api } from "@/lib/api/api";
import { resourceSaveErrorMessage } from "@/lib/dedicated-user-errors";
import { toEntity } from "@/lib/utils";
import { BuilderResourceGeneralFields } from "./BuilderResourceGeneralFields";
import { capacityRequestFields } from "./capacityRequestFields";

/**
 * Single-resource edit form for the map builder's resources panel. Renders
 * the shared BuilderResourceGeneralFields layout and saves with the same
 * semantics as the "Edit Workspace" drawer (resourcePut + cover image +
 * amenity tags, preserving map_data).
 */
export const BuilderResourceForm: React.FC<{
  resource: IResource;
  mutate: () => void;
  onResourcesSaved?: (
    updates: { id: string; name: string; label?: string | null }[]
  ) => void;
  /** Whether the parent picker is shown. */
  canMove: boolean;
}> = ({ resource, mutate, onResourcesSaved, canMove }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [deleteModal, setDeleteModal] = React.useState<IResource[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const toast = useLoadingToast({ loadingMessage: "Applying changes..." });
  const { areas: areaList } = useAreas(resource.office_id ?? null);

  const entity = toEntity(resource.type) || "workspace";
  const isMeetingRoom = entity === "meeting_room";

  const form = useForm<EditResourceForm>({
    defaultValues: {
      name: resource.name ?? "",
      label: resource.label,
      description: resource.description,
      is_multiple: !isMeetingRoom && resource.total_slots > 1,
      parent_id:
        resource.parent_id && areaList?.byId[resource.parent_id]
          ? resource.parent_id
          : null,
      total_slots: resource.total_slots ?? 1,
      maximum_slots_per_user: resource.maximum_slots_per_user ?? null,
      is_open: resource.is_open,
      is_approvable: resource.is_approvable ?? false,
      ticket_type_ids: undefined,
      is_walkup: !!resource.is_walkup,
      is_unavailable: !(resource.is_available ?? true),
      cover_image_file: null,
      amenities: resource.tags ? resource.tags.map((tag) => tag.id) : []
    }
  });

  const onSubmit = async (data: EditResourceForm) => {
    setIsSaving(true);
    toast.startLoading();

    if (data.cover_image_file) {
      await api.client.organizations.resourceMediaUpload(
        resource.id,
        data.cover_image_file,
        true
      );
      form.setValue("cover_image_file", null);
    }

    // Dedicated resources keep their booking invariants (is_open false,
    // untouched booking flags). Capacity is the exception for desks and
    // workspaces: it is how a shared workspace gets its seats.
    const isDedicated = !!resource.is_dedicated;
    const request: EditResourceRequest = {
      ...capacityRequestFields(
        resource,
        getResourceType(entity, !!data.is_multiple),
        {
          isMultiple: !!data.is_multiple,
          totalSlots: data.total_slots,
          isMeetingRoom
        }
      ),
      parent_id: data.parent_id ? data.parent_id : null,
      name: data.name,
      maximum_slots_per_user: data.maximum_slots_per_user ?? 1,
      is_open: isDedicated ? false : resource.is_open,
      // Pass through so a General save never clears a dedicated resource.
      is_dedicated: resource.is_dedicated,
      is_approvable: isDedicated ? resource.is_approvable : data.is_approvable,
      // The picker is hidden for a dedicated resource, so nothing was loaded to
      // send — omitting it leaves the stored set alone.
      ticket_type_ids: isDedicated
        ? undefined
        : data.is_approvable
          ? data.ticket_type_ids
          : [],
      is_walkup: isDedicated ? false : data.is_walkup,
      is_available: isDedicated ? resource.is_available : !data.is_unavailable,
      is_shareable: resource.is_shareable,
      weight: resource.weight || 0,
      map_url: resource.map_url,
      map_data: resource.map_data,
      label: data.label,
      description: data.description
    };

    // resourcePut throws on a 4xx; catching it keeps the form from hanging on
    // "Applying changes…" and lets a capacity refusal say why.
    let ok = false;
    let saveError: unknown = null;
    try {
      ({ ok } = await api.client.organizations.resourcePut(
        resource.id,
        request
      ));
    } catch (error) {
      saveError = error;
      logError("Error saving resource:", error);
    }

    // Only touch amenities after a successful save — clearing tags on a
    // failed PUT would silently wipe them.
    if (ok) {
      await api.client.tags.removeAllTagsFromResource(resource.id);
      if (data.amenities.length > 0) {
        await api.client.tags.addTagToResource(
          resource.id,
          data.amenities.map((id) => ({ tag_id: id }))
        );
      }
    }

    setIsSaving(false);
    if (ok) {
      toast.success("Changes saved");
      onResourcesSaved?.([
        { id: resource.id, name: request.name, label: request.label }
      ]);
    } else {
      toast.error(resourceSaveErrorMessage(saveError));
    }
    mutate();
  };

  return (
    <form
      className="flex h-full flex-col"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <DeleteResourceModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
      />

      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <BuilderResourceGeneralFields
          form={form}
          resource={resource}
          showMedia
          canMove={canMove}
        />
      </div>

      {/* Footer */}
      <div className="flex flex-none gap-2 border-neutral-200 border-t pt-3">
        <button
          type="submit"
          disabled={isSaving}
          className="h-9 flex-1 rounded-md bg-primary font-medium text-sm text-white hover:opacity-90 disabled:opacity-60"
        >
          {isSaving ? <LoadingSpinner color="text-white" /> : "Save"}
        </button>
        {hasEnabledDeleteResource(authenticatedUser) && (
          <button
            type="button"
            disabled={isSaving}
            onClick={() => setDeleteModal([resource])}
            className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50"
            title="Delete resource"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </form>
  );
};
